// If you don't want to use TypeScript you can delete this file!
import React, { useRef, useState, useEffect } from 'react'
import { Link } from 'gatsby'
import cx from 'classnames'
import { useIntersection } from 'react-use'

import BlockContent from 'src/components/global/BlockContent'

import { SmallArrow } from 'src/components/svgs'
import { SEO } from 'src/components/seo'
import { generateSchema, SchemaProps } from 'src/components/schema'
import {
  EditorialFootnoteSerializer,
  createEditorialSerializer,
  getEditorialAnchorSlug,
} from 'src/utils/editorialSerializer'
import {
  EditorialHeader,
  EditorialHeaderProps,
} from 'src/components/editorial/editorialHeader'
import {
  GuideHeader,
  GuideHeaderProps,
} from 'src/components/editorial/guideHeader'
import Content from 'src/components/content'
import { FloatingToc, TocItem } from 'src/components/global/floatingToc'
import { useMediaQuery } from 'src/hooks/useMediaQuery'

export interface EditorialProps {
  pageContext: {
    main: {
      modules: []
      articleType?: {
        slug: {
          current: string
        }
        title: string
      }
      editorialText: any[]
      articleColor: string
      title: string
      subTitle?: string
      publishedAt?: string
      updatedAt?: string
      image: {
        asset: {
          _id: string
        }
      }
    }
    meta: {}
    metaInformation?: {
      metaDescription?: string
      metaKeywords?: string
    }
    env: 'production' | 'development' | 'local' | 'staging'
    pagePath?: string
    documentCreatedAt?: string
    documentUpdatedAt?: string
  }
  path: string
}

const extractEditorialTocItems = (editorialText: any[] = []): TocItem[] =>
  editorialText
    .filter(block => block?._type === 'sectionHeader' && block?.title)
    .map(block => ({
      id: getEditorialAnchorSlug(block.title),
      label: block.title,
    }))
    .filter(item => item.id)

const Editorial = ({ path, pageContext }: EditorialProps) => {
  const {
    main: { modules, editorialText, backgroundColor, articleType },
    metaInformation,
    env,
  } = pageContext
  const { main } = pageContext
  const analyticsContext = {
    module: 'Editorial article body',
    heading: main?.title || 'Editorial article',
  }
  const tocItems = extractEditorialTocItems(editorialText)
  const { isMobileDevice } = useMediaQuery()

  const ref = useRef(null)
  const [fixed, setFixed] = useState(false)

  const intersection = useIntersection(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  })

  useEffect(() => {
    setTimeout(() => {
      if (window.pageXOffset < 200) {
        setFixed(false)
      }
    }, 300)
  }, [0])

  const handleFixed = (value: boolean) => {
    if (window.pageXOffset < 200) {
      setFixed(value)
    } else {
      setFixed(false)
    }
  }

  useEffect(() => {
    intersection &&
    intersection.intersectionRatio < 1 &&
    intersection.boundingClientRect.y > 0
      ? handleFixed(false)
      : handleFixed(true)
  }, [intersection])

  useEffect(() => {
    const footNotes = document.querySelectorAll('.js-footnote')
    const footNotesBottom = document.querySelectorAll('.js-footnote-bottom')
    const footNoteReference = document.querySelectorAll(
      '.js-footnote-reference'
    )
    if (footNotes.length > 0) {
      footNotes.forEach((note, index) => {
        footNoteReference[0].classList.remove('hide')
        const text = `${index + 1}`
        footNotes[index].innerHTML = text
        if (footNotesBottom[index]) {
          footNotesBottom[index].innerHTML = text.concat('.')
        }
      })
    }
  }, [0])

  const pagePath = pageContext.pagePath || path
  const publishedAt = main.publishedAt || pageContext.documentCreatedAt
  const updatedAt =
    main.updatedAt ||
    main.publishedAt ||
    pageContext.documentUpdatedAt ||
    pageContext.documentCreatedAt
  const schema = generateSchema(({
    ...main,
    pagePath,
    description: metaInformation?.metaDescription || main.subTitle,
    keywords: metaInformation?.metaKeywords,
    publishedAt,
    updatedAt,
  } as unknown) as SchemaProps)

  return (
    <div
      className={cx('editorial', {
        'cb bcw': articleType?.slug.current !== 'guide',
        'cw bcpurple': articleType?.slug.current === 'guide',
        sidebar: fixed,
      })}
    >
      <SEO
        metaInfo={metaInformation}
        pagePath={pagePath.replace(/^\/+/, '')}
        env={env}
        schema={schema}
      />
      <div className="bcb">
        <Link
          to="/tiatake/"
          className="container--l f jcs aic cw mxa outer p1 bold"
        >
          <SmallArrow className="block cw rotate" /> The Tia Take
        </Link>
      </div>
      {articleType?.slug.current === 'guide' ? (
        <GuideHeader {...((main as unknown) as GuideHeaderProps)} />
      ) : (
        <EditorialHeader
          {...((main as unknown) as EditorialHeaderProps)}
          publishedAt={publishedAt}
          updatedAt={updatedAt}
        />
      )}
      <div className="editorial__core editorial__core--article f js-editorial jcb rel">
        <div className="editorial__sizer" ref={ref} />
        <div className="x">
          <BlockContent
            blocks={editorialText}
            serializers={createEditorialSerializer(analyticsContext)}
          />
        </div>
      </div>
      <div className="editorial__references py1">
        <div className="container--l outer px1 mxa  rel f jce">
          <div className="container--e x">
            <BlockContent
              blocks={editorialText}
              serializers={EditorialFootnoteSerializer}
            />
          </div>
        </div>
      </div>
      <div className="x z2 rel">
        <Content modules={modules}></Content>
      </div>
      {isMobileDevice && fixed && tocItems.length > 0 && (
        <FloatingToc items={tocItems} />
      )}
    </div>
  )
}

export default Editorial
